/**
 * Hook for fetching all equipment descendants of an asset along with their
 * inline property values, used by the client-side recomposition of the
 * equipment tab (spec 2026-05-05-03).
 *
 * Backed by `GET /api/v1/organizations/{orgCode}/assets?ancestor_uid=<uid>&include=properties,relations`,
 * paginated. The hook returns the full descendant list so consumers can
 * filter and group client-side.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'

export interface AssetEquipmentProperty {
  uid: string
  code: string
  value?: unknown
  source?: string
}

export interface AssetEquipmentItem {
  uid: string
  name?: string | null
  asset_type_uid?: string | null
  parent_asset_uid?: string | null
  depth?: number | null
  /** Asset.quantity — present on every row, defaulted to 1 by the backend. */
  quantity?: number | null
  /** Validity-start column (ISO `YYYY-MM-DD`) — an equipment's commissioning
   *  date since spec 2026-09-03-02. */
  start_date?: string | null
  /** May be omitted by the server when an asset has no current property
   *  values; consumers must handle the undefined case. */
  properties?: AssetEquipmentProperty[]
  /** Embedded relations block (?include=relations). Used to explain why an
   *  asset physically parented elsewhere shows up in this subtree: it is a
   *  member of an aggregating relation whose assembly lives here
   *  (spec 2026-08-16-03 §6). */
  relations?: {
    outgoing?: Array<{
      relation_type?: string
      relation_type_details?: { aggregating?: boolean } | null
      target?: { uid: string; name?: string | null } | null
    }>
  } | null
}

const PAGE_SIZE = 200
/**
 * Budget for the *tree* stream (floors, common areas, lots, rooms). It is the
 * bulk of a subtree and only feeds parent labels and the rollup-boundary rule,
 * so it is the stream that may be cut. Equipment is fetched separately and is
 * never sacrificed to it — see fetchAllEquipment.
 */
const TREE_CAP = 1000
/** Budget for the equipment stream itself. Reaching it is a real anomaly. */
const EQUIPMENT_CAP = 2000

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem('authState')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { accessToken?: string }
    return parsed.accessToken || null
  } catch {
    return null
  }
}

async function fetchPage(
  org: string,
  ancestorUid: string,
  page: number,
  inTree?: boolean,
): Promise<{ data: AssetEquipmentItem[]; total: number }> {
  const params = new URLSearchParams()
  params.set('ancestor_uid', ancestorUid)
  params.set('include', 'properties,relations')
  params.set('limit', String(PAGE_SIZE))
  params.set('page', String(page))
  // `in_tree` filters on the asset's OWN type: false = equipment and other
  // out-of-tree elements, true = the spatial tree. Omitted = both.
  if (inTree !== undefined) params.set('in_tree', String(inTree))

  const url = `${apiBaseUrl}/api/v1/organizations/${encodeURIComponent(org)}/assets?${params.toString()}`
  const token = getAuthToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(url, { headers, credentials: 'include' })
  if (!res.ok) {
    const err = new Error(`useAssetEquipment fetch failed: ${res.status} ${res.statusText}`) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  const body = (await res.json()) as { data: AssetEquipmentItem[]; total?: number }
  return { data: body.data || [], total: body.total ?? body.data?.length ?? 0 }
}

/** Page one `in_tree` stream to exhaustion or to `cap`, whichever comes first. */
async function fetchStream(
  org: string,
  ancestorUid: string,
  inTree: boolean,
  cap: number,
): Promise<{ items: AssetEquipmentItem[]; total: number; truncated: boolean }> {
  const first = await fetchPage(org, ancestorUid, 1, inTree)
  const items: AssetEquipmentItem[] = first.data.slice(0, cap)
  let nextPage = 2
  while (items.length < first.total && items.length < cap) {
    const next = await fetchPage(org, ancestorUid, nextPage, inTree)
    if (next.data.length === 0) break
    for (const item of next.data) {
      if (items.length >= cap) break
      items.push(item)
    }
    nextPage += 1
  }
  return { items, total: first.total, truncated: first.total > items.length }
}

/**
 * Fetch the subtree as two independent streams and concatenate them.
 *
 * A single stream capped at 1000 was not merely slow, it was wrong: on a site
 * or a copropriété with more than a thousand descendants (rooms, lots, mailbox
 * locks — 1195 and 1117 on the SO-OH dossier) the cap fell in the middle of the
 * list and took an arbitrary share of the *equipment* with it, while nothing in
 * the UI said so. Splitting on `in_tree` makes the loss impossible to land on
 * equipment: it is fetched on its own budget, and only the tree stream — which
 * feeds parent labels and the rollup-boundary rule — can be cut.
 *
 * Both streams are needed. The tree assets are not decoration: recomposition
 * reads them to name an equipment's rattachement and to detect a `building` or
 * `unit` sitting strictly between an equipment and the viewed root, which is
 * what stops a lot's equipment from rolling up to its building.
 */
async function fetchAllEquipment(org: string, ancestorUid: string): Promise<{
  equipment: AssetEquipmentItem[]
  total: number
  truncated: boolean
  equipmentTruncated: boolean
}> {
  const equipment = await fetchStream(org, ancestorUid, false, EQUIPMENT_CAP)
  const tree = await fetchStream(org, ancestorUid, true, TREE_CAP)
  return {
    equipment: [...equipment.items, ...tree.items],
    total: equipment.total + tree.total,
    truncated: equipment.truncated || tree.truncated,
    equipmentTruncated: equipment.truncated,
  }
}

/**
 * Returns every descendant asset of `assetUid` together with its inline
 * properties. Pages internally up to a 1000-item cap; sets `truncated=true`
 * when the underlying list is bigger.
 */
/**
 * Invalidate every cached equipment list of the org.
 *
 * Editing an equipment property (condition, commissioning year) mutates a
 * *child* asset, but the Equipment tab reads the list keyed by the *ancestor*
 * it is rendered for. The editing row has no reliable handle on that ancestor,
 * so we invalidate the org's equipment lists wholesale — at most one is
 * mounted at a time, so this refetches exactly the list the user is looking at.
 */
export function refreshAssetEquipmentLists(queryClient: QueryClient, org: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as readonly unknown[]
      return key.length >= 2 && key[0] === 'asset-equipment' && key[1] === org
    },
  })
}

export function useAssetEquipment(org: string, assetUid: string | undefined) {
  const result = useQuery({
    queryKey: ['asset-equipment', org, assetUid ?? ''] as const,
    queryFn: () => fetchAllEquipment(org, assetUid as string),
    enabled: !!assetUid,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  return {
    ...result,
    equipment: result.data?.equipment ?? [],
    total: result.data?.total ?? 0,
    /** Either stream hit its cap. */
    truncated: result.data?.truncated ?? false,
    /** The equipment stream itself was cut — the list is incomplete, not just its labels. */
    equipmentTruncated: result.data?.equipmentTruncated ?? false,
  }
}
